import { lazy, Suspense } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import Index from "./pages/Index";

// Lazy-load toast renderers (not needed until a user action triggers one)
const Toaster = lazy(() =>
  import("@/components/ui/toaster").then((m) => ({ default: m.Toaster }))
);
const Sonner = lazy(() =>
  import("@/components/ui/sonner").then((m) => ({ default: m.Toaster }))
);

const ExperienceDetail = lazy(() => import("./pages/ExperienceDetail"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const Checkout = lazy(() => import("./pages/Checkout"));
const BookingConfirmationPage = lazy(() => import("./pages/BookingConfirmationPage"));
const AdminImages = lazy(() => import("./pages/AdminImages"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const Careers = lazy(() => import("./pages/Careers"));
const Press = lazy(() => import("./pages/Press"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const SafetyInformation = lazy(() => import("./pages/SafetyInformation"));
const CancellationPolicy = lazy(() => import("./pages/CancellationPolicy"));
const CovidResponse = lazy(() => import("./pages/CovidResponse"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Sitemap = lazy(() => import("./pages/Sitemap"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Suspense fallback={null}>
        <Toaster />
        <Sonner />
      </Suspense>
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/experience/:slug" element={<ExperienceDetail />} />
            <Route path="/category/:slug" element={<CategoryPage />} />
            <Route path="/checkout/:id" element={<Checkout />} />
            <Route path="/booking-confirmation/:id" element={<BookingConfirmationPage />} />
            <Route path="/admin/images" element={<AdminImages />} />

            {/* Footer Pages - Company */}
            <Route path="/about" element={<AboutUs />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/press" element={<Press />} />
            <Route path="/stories" element={<Blog />} />
            <Route path="/stories/:slug" element={<BlogPost />} />

            {/* Footer Pages - Support */}
            <Route path="/help" element={<HelpCenter />} />
            <Route path="/safety" element={<SafetyInformation />} />
            <Route path="/cancellation" element={<CancellationPolicy />} />
            <Route path="/covid" element={<CovidResponse />} />

            {/* Footer Pages - Legal */}
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/sitemap" element={<Sitemap />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
